import React, { useCallback, useState, forwardRef, useEffect } from "react";

// assets
import { ChevronDown, CheckIcon, Globe } from "lucide-react";
import { CircleFlag } from "react-circle-flags";

// data
import { countries } from "country-data-list";

// components
import { cn } from "../../utils/tailwind";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import { Label } from "../../components/ui/label";

const SELECTED_LATAM_COUNTRIES = [
  "ARG",
  "CHL",
  "BOL",
  "BRA",
  "COL",
  "ECU",
  "MEX",
  "PER",
  "PRY",
  "URY",
  "VEN",
];
const COUNTRY_OPTIONS = countries.all.filter((country) =>
  SELECTED_LATAM_COUNTRIES.includes(country.alpha3)
);

const CountryDropdownComponent = (
  {
    options = COUNTRY_OPTIONS,
    onChange,
    defaultValue = "ARG",
    disabled = false,
    placeholder = "Selecciona un país",
    slim = false,
    ...props
  },
  ref
) => {
  const [open, setOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(undefined);

  useEffect(() => {
    if (defaultValue) {
      const initialCountry = options.find(
        (country) => country.alpha3 === defaultValue
      );
      if (initialCountry) {
        setSelectedCountry(initialCountry);
        onChange?.(initialCountry.countryCallingCodes?.[0]);
      } else {
        // Reset selected country if defaultValue is not found
        setSelectedCountry(undefined);
      }
    } else {
      // Reset selected country if defaultValue is undefined or null
      setSelectedCountry(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback(
    (country) => {
      setSelectedCountry(country);
      onChange?.(country.countryCallingCodes?.[0]);
      setOpen(false);
    },
    [onChange]
  );

  const triggerClasses = cn(
    "flex h-10 w-28 items-center justify-between whitespace-nowrap rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
    slim === true && "w-20"
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        ref={ref}
        className={triggerClasses}
        disabled={disabled}
        {...props}
      >
        {selectedCountry ? (
          <div className="flex items-center flex-grow w-0 gap-2 overflow-hidden">
            <div className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
              <CircleFlag
                countryCode={selectedCountry.alpha2.toLowerCase()}
                height={20}
              />
            </div>
            {slim === false && (
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {selectedCountry.countryCallingCodes[0]}
              </span>
            )}
          </div>
        ) : (
          <span>
            {slim === false ? (
              placeholder || selectedCountry?.name.split(",")[0]
            ) : (
              <Globe size={20} />
            )}
          </span>
        )}
        <ChevronDown size={16} />
      </PopoverTrigger>
      <PopoverContent
        collisionPadding={10}
        side="bottom"
        className="min-w-[--radix-popper-anchor-width] p-0"
      >
        <Command className="w-full max-h-[200px] sm:max-h-[270px]">
          <CommandList>
            <div className="sticky top-0 z-10 bg-white">
              <CommandInput placeholder="Buscar país..." />
            </div>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {options
                .filter((x) => x.name)
                .map((option, key) => (
                  <CommandItem
                    className="flex items-center w-full gap-2"
                    key={key}
                    onSelect={() => handleSelect(option)}
                  >
                    <div className="flex flex-grow w-0 space-x-2 overflow-hidden">
                      <div className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
                        <CircleFlag
                          countryCode={option.alpha2.toLowerCase()}
                          height={20}
                        />
                      </div>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {option.name.split(",")[0]} (
                        {option.countryCallingCodes[0]})
                      </span>
                    </div>
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4 shrink-0",
                        option.name === selectedCountry?.name
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

CountryDropdownComponent.displayName = "CountryDropdownComponent";

export const CountryDropdown = forwardRef(CountryDropdownComponent);

export const CountryDropdownInput = ({
  label,
  id,
  name,
  containerClassName,
  ...props
}) => {
  return (
    <div className={`space-y-2 ${containerClassName || ""}`}>
      {label && (
        <Label htmlFor={id || name} className="text-sm font-semibold">
          {label}
        </Label>
      )}
      <CountryDropdown {...props} />
    </div>
  );
};
